import React, { useState, useEffect } from 'react'
import queryString from 'query-string'
import InputField from '@components/Shared/InputField/InputField'

import { useAuth } from '@auth/auth-context'
import {
  DEFAULT_ROUTE,
  REGISTER_V2, // TODO: Remove once new sign up flow is complete
} from '@constants/routes'

import blueLogo from '@assets/blue-logo.png'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
/// UI components
import { Button, notification } from 'antd'

/// routing
import { Redirect, Link } from 'react-router-dom'

/// styles
import './Login.scss'

import { isSafeInternalPath } from '@helpers/urlSafety'

// logo style for IE 10
import { isIE10, logoStyleForIE10 } from '@helpers/crossBrowser'
isIE10 && logoStyleForIE10()

const FormItem = Form.Item

const defaultFields = {
  username: '',
  password: '',
}

const Login = ({ location: { search, state } }) => {
  const { isAuthenticated, login, error: authError, clearError } = useAuth()
  const [shouldRedirect, setShouldRedirect] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [fields, setFields] = useState(defaultFields)
  const { token } = queryString.parse(search)

  useEffect(() => {
    if (!isAuthenticated && state) {
      const { success, error } = state || {}
      if (success) {
        notification.success({ message: success })
      }
      if (error) {
        notification.error({ message: error })
      }
    }

    if (isAuthenticated || token) {
      setShouldRedirect(true)
      setFields(defaultFields)
    }
  }, [isAuthenticated, token, state])

  useEffect(() => {
    if (authError) {
      if (authError.includes && authError.includes('Too many')) {
        notification.error({
          message: 'Too many login attempts',
          description: 'Please try again later.',
        })
      } else if (authError.includes && authError.includes('Bad Hash')) {
        notification.error({
          message: 'Expired Password',
          description: 'Please reset your password',
        })
      } else {
        notification.error({
          message: 'Login Details Incorrect',
          description: 'Your login details were not correct.',
        })
      }
      clearError()
    }
  }, [authError, clearError])

  const getRedirectObject = () => {
    // If the user arrived from Pylon — the marketing-site platform chooser carried
    // ?provider=pylon&redirect_uri=<Pylon callback> onto the login URL — continue into the
    // SSO interstitial so the inbound Pylon flow can finish (mint the JWT, hand it back to
    // Pylon), instead of the default landing. Covers both a fresh login and an already
    // signed-in user landing here (both redirect through this one chokepoint). BOTS-51.
    const { provider, redirect_uri: redirectUri } = queryString.parse(search)
    if (provider === 'pylon' && redirectUri) {
      return {
        pathname: '/sso-login',
        search: `?provider=pylon&redirect_uri=${encodeURIComponent(redirectUri)}`,
      }
    }

    const { from: { pathname } = {} } = state || {}
    let path = pathname
    const { next } = queryString.parse(search)
    // `next` comes from the query string on a public route, so honor it only
    // when it points inside this app.
    if (isSafeInternalPath(next)) {
      // Manually split `next` in case it contains a query string.
      const splitRoute = next.split('?')
      let newSearch = splitRoute[1]
      if (newSearch) {
        newSearch = `?${newSearch}`
      }
      path = splitRoute[0]
      return {
        pathname: path,
        search: newSearch,
      }
    }
    if (!isSafeInternalPath(path)) {
      path = `/${DEFAULT_ROUTE}`
    }

    return {
      pathname: path,
      search,
    }
  }

  const onInputChange = ({ name, value }) => {
    fields[name] = value
    setFields(fields)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsFetching(true)
    try {
      await login({ username: fields.username, password: fields.password })
    } catch {
      // Error is handled via authError effect
    } finally {
      setIsFetching(false)
    }
  }

  if (shouldRedirect) {
    return <Redirect to={getRedirectObject()} />
  }

  if (isAuthenticated) {
    return (
      <div>
        You are already logged in
        <Redirect
          to={{
            ...getRedirectObject(),
            state,
          }}
        />
      </div>
    )
  }

  return (
    <div className="login">
      <div className="shadowWrapper">
        <img src={blueLogo} alt={blueLogo} className="login-form-logo" />
        <Form className="login-form" onSubmit={handleSubmit} data-testid="login-form">
          <FormItem>
            <InputField
              placeholder="Email"
              name="username"
              value={fields.username}
              icon="user"
              onChange={onInputChange}
              className="loginfield"
              disabled={isFetching}
              data-testid="login-email-input"
            />
          </FormItem>
          <FormItem>
            <InputField
              placeholder="Password"
              name="password"
              value={fields.password}
              onChange={onInputChange}
              icon="lock"
              type="password"
              className="loginfield"
              disabled={isFetching}
              data-testid="login-password-input"
            />
          </FormItem>
          <FormItem className="altLogin">
            <Button className="login-button" type="primary" htmlType="submit" loading={isFetching} data-testid="login-submit-button">
              Sign in
            </Button>
            <Link to="/users/reset-password" data-testid="login-reset-password-link">
              <Button className="reset-button">Reset Password</Button>
            </Link>
          </FormItem>
          <div className="loginFormBottom">
            <p>
              <span>First time here?&nbsp;</span>
              <Link to={REGISTER_V2} data-testid="login-create-account-link">Create your account</Link>
            </p>
          </div>
          <div className="privacy-policy-container">
            <a
              className="privacy-policy-text"
              onClick={() =>
                // eslint-disable-next-line no-restricted-properties -- fixed marketing URL, not user input
                window.open('https://www.reggora.com/privacy-policy', '_blank', 'noopener,noreferrer')
              }
            >
              Privacy Policy
            </a>
          </div>
        </Form>
      </div>

      <div className="signin-presentation"></div>
    </div>
  )
}

export default Login
